/**
 * Single-cell KPI tile used by the dashboard. Renders a label, a big
 * numeric value, and an optional sublabel / accent color. Designed so
 * that the same component services every count in the top row — adding
 * a new KPI is a one-import job.
 *
 * Visual variants map to the SIGNAL_COLOR palette from the DAG view so
 * that "完了 12" reads in the same green there and on this page.
 */
import { cn } from '../../lib/utils';

export type KpiTone = 'neutral' | 'done' | 'progress' | 'blocked' | 'warning';

const TONE_CLASSES: Record<KpiTone, { ring: string; label: string; value: string }> = {
  neutral: {
    ring: 'border-slate-200 bg-white',
    label: 'text-slate-500',
    value: 'text-slate-900',
  },
  done: {
    ring: 'border-emerald-200 bg-emerald-50',
    label: 'text-emerald-700',
    value: 'text-emerald-900',
  },
  progress: {
    ring: 'border-amber-200 bg-amber-50',
    label: 'text-amber-700',
    value: 'text-amber-900',
  },
  blocked: {
    ring: 'border-rose-200 bg-rose-50',
    label: 'text-rose-700',
    value: 'text-rose-900',
  },
  warning: {
    ring: 'border-orange-200 bg-orange-50',
    label: 'text-orange-700',
    value: 'text-orange-900',
  },
};

export interface KpiCardProps {
  label: string;
  value: number | string;
  /** Optional smaller text shown under the big value (e.g. "/12"). */
  sublabel?: string;
  /** Tone variant; defaults to neutral. */
  tone?: KpiTone;
  /** Optional one-line description shown under the label. */
  hint?: string;
  className?: string;
  testId?: string;
}

export function KpiCard({
  label,
  value,
  sublabel,
  tone = 'neutral',
  hint,
  className,
  testId,
}: KpiCardProps): JSX.Element {
  const toneClasses = TONE_CLASSES[tone];
  return (
    <div
      data-testid={testId ?? 'kpi-card'}
      className={cn(
        'flex flex-col gap-1 rounded-lg border px-3 py-2 shadow-sm',
        toneClasses.ring,
        className,
      )}
    >
      <div className={cn('text-[11px] font-medium uppercase tracking-wide', toneClasses.label)}>
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className={cn('text-2xl font-semibold tabular-nums', toneClasses.value)}>
          {value}
        </span>
        {sublabel ? (
          <span className={cn('text-xs tabular-nums', toneClasses.label)}>{sublabel}</span>
        ) : null}
      </div>
      {hint ? <div className="text-[11px] text-slate-500">{hint}</div> : null}
    </div>
  );
}
